import { useCallback, useEffect, useState } from 'react';
import {
  AlertCircle,
  Download,
  FileSpreadsheet,
  Loader2,
  MonitorCheck,
  Search,
  X,
} from 'lucide-react';

import Header from '../../../dashboard/components/Header';
import ReportSidebar from '../../AccessAdministration/pages/ReportSidebar';

import { getTrackingReport } from '../services/publicReport.service';

import type {
  TrackingReportRecord,
  TrackingReportRequest,
} from '../types/publicReport.types';

const PAGE_SIZE = 100;

const columns: { key: keyof TrackingReportRecord; label: string }[] = [
  { key: 'globaL_ID', label: 'Global ID' },
  { key: 'localID', label: 'Local ID' },
  { key: 'employeeName', label: 'Employee Name' },
  { key: 'status', label: 'Status' },
  { key: 'installationStatus', label: 'Installation Status' },
  { key: 'isDataCaptured', label: 'Data Captured' },
  { key: 'lastDataCapturedAt', label: 'Last Data Captured At' },
  { key: 'hostName', label: 'Host Name' },
  { key: 'ntid', label: 'NTID' },
  { key: 'buildNumber', label: 'Build Number' },
];

const escapeCsvValue = (value: unknown): string => {
  const text = String(value ?? '');
  return `"${text.replace(/"/g, '""')}"`;
};

const formatDate = (value: string | null): string => {
  if (!value) return '';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleString('en-IN');
};

const TrackingReportPage = () => {
  const [searchInput, setSearchInput] = useState('');
  const [appliedSearch, setAppliedSearch] = useState<string | null>(null);
  const [records, setRecords] = useState<TrackingReportRecord[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');

  const totalPages = Math.max(1, Math.ceil(totalRecords / PAGE_SIZE));

  const loadReport = useCallback(
    async (page: number, searchText: string | null) => {
      setLoading(true);
      setError('');

      const payload: TrackingReportRequest = {
        PageNumber: page,
        PageSize: PAGE_SIZE,
        SearchText: searchText?.trim() || null,
      };

      try {
        const response = await getTrackingReport(payload);
        setRecords(Array.isArray(response.records) ? response.records : []);
        setTotalRecords(Number(response.totalRecords) || 0);
        setPageNumber(page);
      } catch (err: unknown) {
        console.error('Tracking Report API error:', err);
        setRecords([]);
        setTotalRecords(0);
        setError(
          err instanceof Error
            ? err.message
            : 'Unable to load the Tracking Report.',
        );
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    void loadReport(1, null);
  }, [loadReport]);

  const handleSearch = () => {
    const normalizedSearch = searchInput.trim() || null;
    setAppliedSearch(normalizedSearch);
    void loadReport(1, normalizedSearch);
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setAppliedSearch(null);
    void loadReport(1, null);
  };

  const handleExport = async () => {
    setExporting(true);
    setError('');

    try {
      const searchText = appliedSearch?.trim() || null;
      const firstPage = await getTrackingReport({
        PageNumber: 1,
        PageSize: PAGE_SIZE,
        SearchText: searchText,
      });

      const allRecords: TrackingReportRecord[] = [
        ...(firstPage.records ?? []),
      ];
      const exportTotal = Number(firstPage.totalRecords) || 0;
      const exportTotalPages = Math.ceil(exportTotal / PAGE_SIZE);

      for (let page = 2; page <= exportTotalPages; page += 1) {
        const response = await getTrackingReport({
          PageNumber: page,
          PageSize: PAGE_SIZE,
          SearchText: searchText,
        });
        allRecords.push(...(response.records ?? []));
      }

      if (allRecords.length === 0) {
        setError('No records available for export.');
        return;
      }

      const headers = columns.map(({ label }) => label);
      const rows = allRecords.map((record) =>
        columns.map(({ key }) => {
          const value = record[key];
          return key === 'lastDataCapturedAt'
            ? formatDate(value as string | null)
            : (value ?? '');
        }),
      );

      // UTF-8 BOM helps Excel open the CSV with the correct encoding.
      const csvContent =
        '\uFEFF' +
        [headers, ...rows]
          .map((row) => row.map(escapeCsvValue).join(','))
          .join('\r\n');

      const blob = new Blob([csvContent], {
        type: 'text/csv;charset=utf-8;',
      });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      const datePart = new Date().toISOString().slice(0, 10);

      link.href = downloadUrl;
      link.download = `Tracking_Report_${datePart}.csv`;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => window.URL.revokeObjectURL(downloadUrl), 1000);
    } catch (err: unknown) {
      console.error('Tracking Report CSV export error:', err);
      setError(
        err instanceof Error
          ? err.message
          : 'Unable to export the Tracking Report.',
      );
    } finally {
      setExporting(false);
    }
  };

  const handlePageChange = (newPage: number) => {
    if (newPage < 1 || newPage > totalPages || loading) return;
    void loadReport(newPage, appliedSearch);
  };

  const startRecord =
    totalRecords === 0 ? 0 : (pageNumber - 1) * PAGE_SIZE + 1;
  const endRecord = Math.min(pageNumber * PAGE_SIZE, totalRecords);

  return (
    <main className="min-h-screen bg-[#eef3f8] text-slate-900">
      <Header />

      <div className="flex min-h-[calc(100vh-72px)]">
        <ReportSidebar />

        <section className="min-w-0 flex-1 p-4 sm:p-6 lg:p-8">
          <div className="mx-auto max-w-[1600px]">
            <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-teal-50 text-teal-700">
                  <MonitorCheck size={25} />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-slate-900">
                    Tracking Report
                  </h1>
                  <p className="mt-1 text-sm text-slate-500">
                    Installation Compliance Report
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={handleExport}
                disabled={exporting || loading || totalRecords === 0}
                className="inline-flex items-center gap-2 rounded-lg bg-teal-700 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-teal-800 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {exporting ? (
                  <Loader2 size={17} className="animate-spin" />
                ) : (
                  <Download size={17} />
                )}
                {exporting ? 'Exporting...' : 'Export CSV'}
              </button>
            </div>

            <div className="mb-5 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <label
                htmlFor="tracking-search"
                className="mb-2 block text-sm font-medium text-slate-700"
              >
                Search Tracking Records
              </label>

              <form
                className="flex flex-col gap-3 sm:flex-row"
                onSubmit={(event) => {
                  event.preventDefault();
                  handleSearch();
                }}
              >
                <div className="relative flex-1">
                  <Search
                    size={18}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                  />
                  <input
                    id="tracking-search"
                    type="text"
                    value={searchInput}
                    onChange={(event) => setSearchInput(event.target.value)}
                    placeholder="Search Global ID, employee, NTID or hostname..."
                    className="w-full rounded-lg border border-slate-300 py-2.5 pl-10 pr-10 text-sm outline-none transition focus:border-teal-600 focus:ring-2 focus:ring-teal-100"
                  />
                  {searchInput && (
                    <button
                      type="button"
                      aria-label="Clear search input"
                      onClick={() => setSearchInput('')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                    >
                      <X size={17} />
                    </button>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="inline-flex items-center justify-center gap-2 rounded-lg bg-slate-900 px-5 py-2.5 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-50"
                >
                  <Search size={16} />
                  Search
                </button>

                <button
                  type="button"
                  onClick={handleClearSearch}
                  disabled={loading}
                  className="rounded-lg border border-slate-300 px-5 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                >
                  Clear
                </button>
              </form>
            </div>

            {error && (
              <div
                role="alert"
                className="mb-4 flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700"
              >
                <AlertCircle size={18} className="mt-0.5 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 px-5 py-4">
                <div>
                  <h2 className="font-semibold text-slate-900">
                    Installation Compliance
                  </h2>
                  <p className="mt-1 text-xs text-slate-500">
                    {appliedSearch
                      ? `Search: ${appliedSearch}`
                      : 'All tracking records'}
                  </p>
                </div>
                <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">
                  {totalRecords.toLocaleString()} records
                </span>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full min-w-[1400px] text-left text-sm">
                  <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                    <tr>
                      {columns.map((column) => (
                        <th
                          key={column.key}
                          className="whitespace-nowrap border-b border-slate-200 px-4 py-3 font-semibold"
                        >
                          {column.label}
                        </th>
                      ))}
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-slate-100">
                    {loading ? (
                      <tr>
                        <td
                          colSpan={columns.length}
                          className="px-4 py-16 text-center text-slate-500"
                        >
                          <Loader2
                            size={24}
                            className="mx-auto mb-3 animate-spin text-teal-700"
                          />
                          Loading tracking records...
                        </td>
                      </tr>
                    ) : records.length === 0 ? (
                      <tr>
                        <td
                          colSpan={columns.length}
                          className="px-4 py-16 text-center text-slate-500"
                        >
                          No tracking records found.
                        </td>
                      </tr>
                    ) : (
                      records.map((record, index) => (
                        <tr
                          key={`${record.globaL_ID}-${index}`}
                          className="transition hover:bg-teal-50/40"
                        >
                          {columns.map(({ key }) => (
                            <td
                              key={key}
                              className="max-w-[300px] whitespace-nowrap px-4 py-3 text-slate-700"
                              title={String(record[key] ?? '')}
                            >
                              {key === 'installationStatus' ? (
                                <span
                                  className={`rounded-full px-2.5 py-1 text-xs font-medium ${
                                    String(record[key] ?? '').toLowerCase() ===
                                    'completed'
                                      ? 'bg-emerald-50 text-emerald-700'
                                      : 'bg-amber-50 text-amber-700'
                                  }`}
                                >
                                  {record[key] || '-'}
                                </span>
                              ) : key === 'lastDataCapturedAt' ? (
                                formatDate(record[key] as string | null) || '-'
                              ) : (
                                record[key] || '-'
                              )}
                            </td>
                          ))}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 px-5 py-4">
                <p className="text-sm text-slate-500">
                  Showing {startRecord} to {endRecord} of {totalRecords} records
                </p>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    disabled={loading || pageNumber <= 1}
                    onClick={() => handlePageChange(pageNumber - 1)}
                    className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    Previous
                  </button>
                  <span className="min-w-[90px] text-center text-xs font-medium text-slate-600">
                    Page {pageNumber} / {totalPages}
                  </span>
                  <button
                    type="button"
                    disabled={loading || pageNumber >= totalPages}
                    onClick={() => handlePageChange(pageNumber + 1)}
                    className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>

            <p className="mt-3 flex items-center gap-2 text-xs text-slate-400">
              <FileSpreadsheet size={15} />
              CSV export includes all records matching the active search.
            </p>
          </div>
        </section>
      </div>
    </main>
  );
};

export default TrackingReportPage;
